import type {
  AlertDialogOptions,
  ConfirmDialogOptions,
  ConfirmDeleteDialogOptions,
  CustomDialogOptions,
  DialogComponent,
  DialogComponentProps,
  DialogBeforeCloseHandler,
  DialogInstance,
} from "./dialog-types";

type Listener = () => void;

let dialogs: DialogInstance[] = [];
const listeners = new Set<Listener>();

function emitChange() {
  listeners.forEach((listener) => listener());
}

function createId() {
  return `dialog-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function getDialog(id: string) {
  return dialogs.find((dialog) => dialog.id === id);
}

function addDialog(dialog: DialogInstance) {
  dialogs = [...dialogs, dialog];
  emitChange();
}

function removeDialog(id: string) {
  dialogs = dialogs.filter((dialog) => dialog.id !== id);
  emitChange();
}

function normalizeOptions<T extends CustomDialogOptions>(options?: T): T {
  return {
    closeOnEsc: true,
    closeOnOutside: false,
    ...options,
  } as T;
}

async function dismiss(id: string) {
  let dialog = getDialog(id);

  if (!dialog || dialog.status === "submitting") {
    return;
  }

  if (dialog.beforeClose && !(await dialog.beforeClose())) {
    return;
  }

  dialog = getDialog(id);
  if (!dialog || dialog.status === "submitting") {
    return;
  }

  removeDialog(id);

  if (dialog.kind === "confirm") {
    dialog.resolveValue(false);
    return;
  }

  if (dialog.kind === "alert") {
    dialog.resolveValue();
    return;
  }

  dialog.resolveValue(null);
}

function setBeforeClose(id: string, handler?: DialogBeforeCloseHandler) {
  const current = getDialog(id);
  if (!current || current.beforeClose === handler) {
    return;
  }

  dialogs = dialogs.map((dialog) =>
    dialog.id === id ? { ...dialog, beforeClose: handler } : dialog,
  );
  emitChange();
}

function resolve(id: string, value?: unknown) {
  const dialog = getDialog(id);

  if (!dialog) {
    return;
  }

  removeDialog(id);
  dialog.resolveValue(value as never);
}

function reject(id: string, reason?: unknown) {
  const dialog = getDialog(id);

  if (!dialog) {
    return;
  }

  removeDialog(id);
  dialog.rejectValue(reason);
}

function setSubmitting(id: string, value: boolean) {
  dialogs = dialogs.map((dialog) =>
    dialog.id === id
      ? {
          ...dialog,
          status: value ? "submitting" : "idle",
        }
      : dialog,
  );
  emitChange();
}

function closeAll() {
  const snapshot = dialogs;
  dialogs = [];
  emitChange();

  snapshot.forEach((dialog) => {
    if (dialog.kind === "confirm") {
      dialog.resolveValue(false);
      return;
    }

    if (dialog.kind === "alert") {
      dialog.resolveValue();
      return;
    }

    dialog.resolveValue(null);
  });
}

function subscribe(listener: Listener) {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function getState() {
  return dialogs;
}

function alert(options: AlertDialogOptions = {}) {
  const id = createId();
  const normalizedOptions = normalizeOptions(options);

  return new Promise<void>((resolveValue, rejectValue) => {
    addDialog({
      id,
      kind: "alert",
      status: "idle",
      confirmText: normalizedOptions.confirmText ?? "确定",
      resolveValue: () => resolveValue(),
      rejectValue,
      ...normalizedOptions,
    });
  });
}

function confirm(options: ConfirmDialogOptions = {}) {
  const id = createId();
  const normalizedOptions = normalizeOptions(options);

  return new Promise<boolean>((resolveValue, rejectValue) => {
    addDialog({
      id,
      kind: "confirm",
      status: "idle",
      confirmText: normalizedOptions.confirmText ?? "确定",
      cancelText: normalizedOptions.cancelText ?? "取消",
      variant: normalizedOptions.variant ?? "default",
      resolveValue: (value?: unknown) => resolveValue(Boolean(value)),
      rejectValue,
      ...normalizedOptions,
    });
  });
}

function confirmDelete(options: ConfirmDeleteDialogOptions) {
  const entityName = options.entityName ? `“${options.entityName}”` : "";
  const subject = [entityName, options.entityLabel].filter(Boolean).join("");
  const consequence = options.consequence ?? "此操作无法撤销。";

  return confirm({
    ...options,
    title: options.title ?? `删除${options.entityLabel}`,
    description:
      options.description ?? `确定要删除${subject}吗？${consequence}`,
    confirmText: "删除",
    cancelText: "取消",
    variant: "destructive",
    closeOnOutside: false,
    closeOnEsc: true,
  });
}

function open<TProps extends object, TResult = void>(
  component: DialogComponent<TProps, TResult>,
  props: Omit<TProps, keyof DialogComponentProps<TResult>>,
  options: CustomDialogOptions = {},
) {
  const id = createId();
  const normalizedOptions = normalizeOptions(options);

  return new Promise<TResult | null>((resolveValue, rejectValue) => {
    addDialog({
      id,
      kind: "custom",
      component: component as DialogComponent<Record<string, unknown>, unknown>,
      props: props as Record<string, unknown>,
      status: "idle",
      resolveValue: (value?: unknown) => resolveValue((value ?? null) as TResult | null),
      rejectValue,
      ...normalizedOptions,
    });
  });
}

export const dialogManager = {
  alert,
  closeAll,
  confirm,
  confirmDelete,
  dismiss,
  getState,
  open,
  reject,
  resolve,
  setSubmitting,
  setBeforeClose,
  subscribe,
};
